/**
 * storyboardParser.js — 分镜文本正则解析器
 *
 * 输入：用户粘贴的一段分镜脚本文本
 * 输出：解析好的分镜数组，每项包含时间标签、名称、提示词内容
 *
 * 🎯 智能格式检测机制：
 *   1. 第一遍扫描：检测文本中第一个出现的分镜标志，确定「主格式模式」
 *   2. 第二遍解析：只按主格式切分，忽略其他格式（避免正文中的"镜头"等词被误判）
 *
 * 支持的主格式模式：
 *   - 第X镜/幕/场（diXjing）：第一镜、第2镜、第三幕
 *   - X镜/幕/场（Xjing）：1镜、2镜、三镜
 *   - 镜X / 镜头X（jingX）：镜1、镜头2、镜头第三
 *   - Scene X / Shot X（scene/shot）：Scene 1、Shot 2
 *   - 时间段（time）：0~3秒、0:00-0:03
 *
 * 识别完成后统一规范时间为 0~3秒 格式
 */

const CN_NUMS = '零一二三四五六七八九十百千两';

// 🔒 锁定格参考图代号：图片说明、锁定格文案、全局指令三处共用同一个名字，
//   保证「模型收到的图名」与「提示词里引用的名」一字不差。改名只改这一处。
export const LOCK_REF_NAME = 'KILLGAME0623';

// 数字段：阿拉伯数字或中文数字
const NUM = `[${CN_NUMS}\\d]+`;

// 范围连接符：- ~ ～ 到 至
const SEP = `[\\-~～到至]`;

// 时间段 mm:ss 格式：0:00-0:03 / 00:00~00:03
const TIME_COLON = `\\d{1,2}:\\d{2}\\s*${SEP}\\s*\\d{1,2}:\\d{2}`;
// 时间段 秒 格式：0-3秒 / 0~3秒 / 0秒-3秒 / 0秒到3秒 / 0s-3s（末尾必须有 秒/s）
const TIME_SEC = `\\d+(?:\\.\\d+)?\\s*[秒s]?\\s*${SEP}\\s*\\d+(?:\\.\\d+)?\\s*[秒s]`;

// 🎯 主格式模式定义：每种格式独立的正则表达式
// 行首允许空白，但不允许其他文字（避免正文中的"镜头"等词被误判）
const PATTERNS = {
  diXjing: new RegExp(`^[^\\S\\r\\n]*第\\s*${NUM}\\s*[镜幕场]`),       // 第X镜/幕/场
  Xjing: new RegExp(`^[^\\S\\r\\n]*${NUM}\\s*[镜幕场](?!${NUM})`),  // X镜/幕/场（后面不能再跟数字，避免匹配"12镜头"）
  jingX: new RegExp(`^[^\\S\\r\\n]*镜(?:头)?\\s*(?:第\\s*)?${NUM}`), // 镜X / 镜头X / 镜头第X
  scene: /^[^\S\r\n]*[Ss]cene\s+\d+/,                               // Scene X
  shot: /^[^\S\r\n]*[Ss]hot\s+\d+/,                                 // Shot X
  timeColon: new RegExp(`^[^\\S\\r\\n]*${TIME_COLON}`),             // 时间段（mm:ss格式）
  timeSec: new RegExp(`^[^\\S\\r\\n]*${TIME_SEC}`),                 // 时间段（秒格式）
};

// 格式优先级（从严格到宽松）：先检测严格格式，避免被宽松格式误判
const PATTERN_ORDER = ['diXjing', 'scene', 'shot', 'timeColon', 'timeSec', 'jingX', 'Xjing'];

/**
 * 把中文数字（零到百）转成阿拉伯数字，供排序/识别用
 */
function cnToNum(str) {
  const map = { 零:0,一:1,二:2,三:3,四:4,五:5,六:6,七:7,八:8,九:9,十:10,百:100,两:2 };
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  let n = 0;
  let tmp = 0;
  for (const ch of str) {
    const v = map[ch] ?? null;
    if (v === null) continue;
    if (v >= 10) {
      n += (tmp || 1) * v;
      tmp = 0;
    } else {
      tmp = v;
    }
  }
  return n + tmp;
}

/**
 * 从分镜起始标志原文中提取并规范化时间标签为 "0~3秒" 格式
 * 支持：0-3秒 / 0~3秒 / 0秒-3秒 / 0秒到3秒 / 0s-3s / 0:00-0:03
 * 无时间信息时返回空字符串
 */
function extractTimeLabel(raw) {
  if (!raw) return '';
  // mm:ss 格式 → 换算为秒
  const colon = raw.match(/(\d{1,2}):(\d{2})\s*[\-~～到至]\s*(\d{1,2}):(\d{2})/);
  if (colon) {
    const a = (+colon[1]) * 60 + (+colon[2]);
    const b = (+colon[3]) * 60 + (+colon[4]);
    return `${a}~${b}秒`;
  }
  // 秒格式
  const sec = raw.match(/(\d+(?:\.\d+)?)\s*[秒s]?\s*[\-~～到至]\s*(\d+(?:\.\d+)?)\s*[秒s]/);
  if (sec) {
    const fmt = n => Number.isInteger(n) ? String(n) : n.toFixed(1);
    return `${fmt(parseFloat(sec[1]))}~${fmt(parseFloat(sec[2]))}秒`;
  }
  return '';
}

/**
 * 🎯 第一遍扫描：检测文本中第一个出现的分镜标志格式，确定「主格式模式」
 *
 * @param {string} text - 原始分镜文本
 * @returns {string|null} - 主格式名（如 'diXjing'、'timeColon'），未找到返回 null
 */
function detectPrimaryPattern(text) {
  if (!text || !text.trim()) return null;

  const lines = text.split(/\r?\n/);

  // 按优先级顺序检测（从严格到宽松）
  for (const line of lines) {
    for (const patternName of PATTERN_ORDER) {
      const regex = PATTERNS[patternName];
      if (regex.test(line)) {
        return patternName;  // 返回第一个匹配的格式名
      }
    }
  }

  return null;  // 未找到任何分镜格式
}

/**
 * 主解析函数 — 按主格式解析
 *
 * 🎯 两步策略：
 *   1. 第一遍扫描：检测文本中第一个出现的分镜标志，确定「主格式模式」
 *   2. 第二遍解析：只按主格式切分，忽略其他格式（避免正文中的"镜头"等词被误判）
 *
 * 规则：逐行扫描，只有「行首」匹配主格式的行才作为新分镜的起点；
 *       其余行（含正文里出现的"镜头"等）都并入当前分镜的内容。
 *
 * @param {string} text      原始分镜脚本文本
 * @param {number} maxCells  宫格数量上限（超出的分镜丢弃）
 * @returns {Array<{name:string, timeLabel:string, prompt:string}>}
 */
export function parseStoryboardText(text, maxCells = 16) {
  if (!text || !text.trim()) return [];

  // 🎯 第一步：检测主格式
  const primaryPattern = detectPrimaryPattern(text);
  if (!primaryPattern) {
    // 未检测到任何分镜格式
    return [];
  }

  // 🎯 第二步：只按主格式解析
  const regex = PATTERNS[primaryPattern];
  const lines = text.split(/\r?\n/);
  const shots = [];        // { timeLabel, contentLines: [] }
  let current = null;

  for (const line of lines) {
    const m = line.match(regex);  // 只匹配主格式
    if (m) {
      // 找到主格式分镜标志 → 开启一个新分镜
      const marker = m[0];
      const timeLabel = extractTimeLabel(marker);

      // 标志后剩余的同行文字作为该镜内容的第一段
      const matchEnd = m.index + m[0].length;
      const rest = line.slice(matchEnd).replace(/^[\s:：、.，。\-（）()【】]+/, '').trim();

      current = { timeLabel, contentLines: rest ? [rest] : [] };
      shots.push(current);
    } else if (current) {
      // 非主格式标志行 → 归入当前分镜内容
      const t = line.trim();
      if (t) current.contentLines.push(t);
    }
    // else: 在第一个分镜之前的文字，丢弃（例如全局说明、前言）
  }

  return shots.slice(0, maxCells).map((s, i) => {
    const content = s.contentLines.join('\n').trim();
    // 过滤掉占位符（空白）
    const finalPrompt = content === '（空白）' ? '' : content;
    return {
      name: `第${i + 1}镜`,
      timeLabel: s.timeLabel,
      prompt: finalPrompt,
    };
  });
}

/**
 * 根据比例字符串判断横竖版方向，返回中文词（竖版 / 横版 / 方形）
 * 模型对 "9:16" 这类数字比例的方向感偶尔会错乱，补一个中文方向词强化。
 * 解析失败（拿不到 w:h）返回空串，调用方跳过不拼。
 */
function getOrientationLabel(ratio) {
  if (!ratio || typeof ratio !== 'string') return '';
  const m = ratio.match(/^(\d+)\s*[:：]\s*(\d+)$/);
  if (!m) return '';
  const w = parseInt(m[1], 10);
  const h = parseInt(m[2], 10);
  if (!w || !h) return '';
  if (w > h) return '横版';
  if (w < h) return '竖版';
  return '方形';
}

/**
 * 根据 gridType 返回排列方式描述
 */
function getGridLayout(gridType) {
  const layouts = {
    4: '2×2（2行、2列）',
    9: '3×3（3行、3列）',
    16: '4×4（4行、4列）',
    25: '5×5（5行、5列）'
  };
  return layouts[gridType] || '未知布局';
}

/**
 * 把格号数组压缩成好读的文字：连续段用范围，其余枚举
 *   [8,9]        → "8、9"
 *   [5,6,7,8,9]  → "5~9"
 *   [1,2,3,7,9]  → "1~3、7、9"
 */
function fmtNums(nums) {
  if (!nums || nums.length === 0) return '';
  const sorted = [...nums].sort((a, b) => a - b);
  const segs = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const n = sorted[i];
    if (n === prev + 1) { prev = n; continue; }
    // 收束一段：单个用本身，连续（≥3）用范围，2 个也枚举更清楚
    if (prev - start >= 2) segs.push(`${start}~${prev}`);
    else if (prev === start) segs.push(`${start}`);
    else segs.push(`${start}、${prev}`);
    start = n;
    prev = n;
  }
  return segs.join('、');
}

/**
 * 把 node.data 里的分镜结构组装成发给大模型的最终 prompt 文本
 *
 * 格式：
 *   {基础提示词1}
 *   {基础提示词2}
 *
 *   按要求生成16:9比例，3×2（3行、2列）分镜表。
 *
 *   0~3秒：{第1格内容}
 *   3~6秒：{第2格内容}
 *   ...
 *
 * @param {Array} basePrompts  [{name, text}, ...]
 * @param {Array} gridCells    [{name, timeLabel, prompt}, ...]
 * @param {string} aspectRatio 比例（如 '16:9'）
 * @param {number} gridType    宫格数（4/9/16/25）
 * @returns {string}
 */
export function assembleStoryboardPrompt(basePrompts = [], gridCells = [], aspectRatio = '1:1', gridType = 9, lockReferenceFed = false) {
  const parts = [];

  // 基础提示词
  for (const bp of basePrompts) {
    if (bp.text && bp.text.trim()) parts.push(bp.text.trim());
  }

  // 🎬 添加比例和宫格排列方式提示（比例 + 横竖版方向 + 排列，方向词强化避免横竖错乱）
  const gridLayout = getGridLayout(gridType);
  const orientation = getOrientationLabel(aspectRatio);
  const ratioText = orientation ? `${aspectRatio}比例（${orientation}）` : `${aspectRatio}比例`;
  // 方向明确（横/竖/方形）时追加一句强化：整图与每格同为该方向，避免模型把竖版画成横版
  const orientEmphasis = orientation ? `整张图与每个宫格都必须是${orientation}方向，不得画成其他方向。` : '';
  let layoutPrompt = `按要求生成${ratioText}、${gridLayout}分镜表。${orientEmphasis}每个分镜画面需完整铺满所在宫格，紧贴格子边缘，不留白边与空隙。`;

  // 精确统计哪几格有内容、哪几格空（格号 1-based，从左到右、从上到下）
  const filled = [];
  const empty = [];
  for (let i = 0; i < gridType; i++) {
    const c = gridCells[i];
    if (c && c.prompt && c.prompt.trim()) filled.push(i + 1);
    else empty.push(i + 1);
  }
  // 有空格时精确点名：让模型明确哪几格画内容、哪几格涂黑，避免自行编造或往前挤
  if (empty.length > 0 && filled.length > 0) {
    layoutPrompt += `本表共${gridType}格（从左到右、从上到下编号）：第${fmtNums(filled)}格有画面内容，第${fmtNums(empty)}格必须整格涂纯黑、保持全黑空白，禁止绘制任何画面。`;
  }
  parts.push(layoutPrompt);

  // 🔒 有锁定格时加一句全局指令：明确只有锁定格照抄「参考图」，其余格照旧按文字提示词重画。
  //   不加这句，模型拿到整张参考图后容易把没锁的格也一起照抄，导致所有宫格都不变。
  if (lockReferenceFed && gridCells.some(c => c && c.locked)) {
    parts.push(`注意：下面标注「这部分与“${LOCK_REF_NAME}”保持一致」的宫格，必须与“${LOCK_REF_NAME}”里对应位置的画面完全相同、不做任何改动；其余宫格请忽略“${LOCK_REF_NAME}”，严格按各自的文字提示词重新绘制。`);
  }

  // 分镜格子：每行带上格号，把内容与物理格位置绑死（防止跳格时内容往前挤）
  const shots = [];
  gridCells.forEach((cell, i) => {
    // 🆕 空格子也要明确输出指令，强制模型涂黑
    if (!cell || !cell.prompt || !cell.prompt.trim()) {
      shots.push(`第${i + 1}格：整格涂纯黑，不绘制任何画面内容。`);
      return;
    }
    // 🔒 锁定格：仅当上一轮整图确实被喂给模型时（lockReferenceFed）才替换。
    //   镜号 + 时间标签照旧保留（如”第1镜 0~5秒”），内容换成「这部分与”参考图”保持一致」。
    //   名字用中文全角引号「”参考图”」，与图片说明里「图N是”参考图”」一字不差 —— 全角引号
    //   在 JSON 里不转义，客户看提示词时不会出现 \” 的斜杠。cell.prompt 原样不动。
    if (lockReferenceFed && cell.locked) {
      const label = [cell.name || `第${i + 1}镜`, cell.timeLabel].filter(Boolean).join(' ');
      shots.push(`第${i + 1}格 ${label}：这部分与”${LOCK_REF_NAME}”保持一致`);
      return;
    }
    const label = cell.timeLabel || cell.name || `第${i + 1}镜`;
    shots.push(`第${i + 1}格 ${label}：${cell.prompt.trim()}`);
  });
  if (shots.length > 0) parts.push(shots.join('\n'));

  return parts.join('\n\n');
}

/**
 * 将宫格内容转换回文本格式（用于右侧文本框显示）
 *
 * @param {Array} gridCells [{name, timeLabel, prompt}, ...]
 * @param {boolean} keepEmpty 是否保留空格子（默认 true，用于三处同步编辑）
 * @returns {string} 格式化的分镜文本
 */
export function gridCellsToText(gridCells = [], keepEmpty = true) {
  const lines = [];
  gridCells.forEach((cell, i) => {
    const name = cell.name || `第${i + 1}镜`;
    const header = cell.timeLabel ? `${name} ${cell.timeLabel}：` : `${name}：`;

    if (cell.prompt && cell.prompt.trim()) {
      // 有内容的格子
      lines.push(`${header}\n${cell.prompt.trim()}`);
    } else if (keepEmpty) {
      // 空格子：输出标题 + 占位符（用于保持结构）
      lines.push(`${header}\n（空白）`);
    }
    // keepEmpty=false 时跳过空格子（用于旧的"整理文本"场景）
  });
  return lines.join('\n\n');
}
